import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { recruitmentApi } from "../../services/recruitmentApi";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { usePermissions } from "../../hooks/usePermissions";
import { getShiftInterestTag, needsAvailabilityReview, getRealInterviewStatusTag } from "../../lib/applicantMatching";
import { statusLabel } from "../../lib/statusLabel";
import { useLocalization } from "../../hooks/useLocalization";

const STATUSES = ["", "NEW", "SCREENING", "INTERVIEW_SCHEDULED", "INTERVIEWED", "OFFER_EXTENDED", "HIRED", "REJECTED", "WITHDRAWN"];

const SHIFT_TAG_STYLES = {
  OPENING: "bg-amber-50 border-amber-200 text-amber-700", CLOSING: "bg-indigo-50 border-indigo-200 text-indigo-700",
  BOTH: "bg-red-50 border-red-200 text-red-700", FLEXIBLE: "bg-emerald-50 border-emerald-200 text-emerald-700",
};
const INTERVIEW_TAG_STYLES = {
  emerald: "text-emerald-600", amber: "text-amber-600", red: "text-red-600", gray: "text-gray-500",
};

export default function RecruitmentApplicantsPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [searchParams] = useSearchParams();
  const jobPostingId = searchParams.get("job_posting_id") || "";
  const { canManageRecruitment } = usePermissions();
  const [applicants, setApplicants] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [interviewFilter, setInterviewFilter] = useState("");
  const [jobs, setJobs] = useState([]);

  const SHIFT_INTEREST_FILTERS = [
    { key: "", label: t("common.all") }, { key: "OPENING", label: t("mgmtRecruitment.opening") }, { key: "CLOSING", label: t("mgmtRecruitment.closing") },
    { key: "BOTH", label: t("mgmtRecruitment.openingAndClosing") }, { key: "FLEXIBLE", label: t("mgmtRecruitment.flexible") }, { key: "AVAILABILITY_REVIEW", label: t("mgmtRecruitment.availabilityReview") },
  ];
  const INTERVIEW_FILTERS = [
    { key: "", label: t("common.all") }, { key: "REQUESTED", label: t("mgmtRecruitment.requested") }, { key: "PENDING_CONFIRMATION", label: t("mgmtRecruitment.pendingConfirmation") },
    { key: "CONFIRMED", label: t("mgmtRecruitment.confirmed") }, { key: "RESCHEDULE_REQUESTED", label: t("mgmtRecruitment.rescheduleRequested") },
    { key: "COMPLETED", label: t("mgmtRecruitment.completed") }, { key: "CANCELLED", label: t("mgmtRecruitment.cancelled") }, { key: "NO_SHOW", label: t("mgmtRecruitment.noShow") },
    { key: "NONE", label: t("mgmtRecruitment.noInterview") },
  ];

  useEffect(() => {
    if (!canManageRecruitment) return;
    recruitmentApi.listApplicants(jobPostingId || null, statusFilter || null).then((r) => setApplicants(r.data)).catch(() => {});
    recruitmentApi.listJobs().then((r) => setJobs(r.data)).catch(() => {});
  }, [canManageRecruitment, jobPostingId, statusFilter]);

  if (!canManageRecruitment) return <p className="text-sm text-gray-500" data-testid="recruitment-applicants-access-denied">{t("common.accessDenied")}</p>;

  const jobTitle = (id) => jobs.find((j) => j.id === id)?.title_en || id;
  const jobStore = (id) => jobs.find((j) => j.id === id)?.store_location || "-";

  const visibleApplicants = applicants.filter((a) => {
    if (shiftFilter === "AVAILABILITY_REVIEW") return needsAvailabilityReview(a);
    if (shiftFilter) return getShiftInterestTag(a)?.key === shiftFilter;
    if (interviewFilter) return getRealInterviewStatusTag(a).key === interviewFilter;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="recruitment-applicants-page">
      <PageHelpBanner pageKey="recruitment-applicants" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtRecruitment.applicantsHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtRecruitment.applicantsTitle")}</h2>
        <p className="text-sm text-gray-500">
          {jobPostingId ? `${t("mgmtRecruitment.filteredTo")}: ${jobTitle(jobPostingId)}` : t("mgmtRecruitment.allPositions")}
          {jobPostingId && (
            <Link to="/management/recruitment/applicants" data-testid="clear-job-filter-link" className="text-blue-600 hover:underline ml-2">{t("mgmtRecruitment.clearFilter")}</Link>
          )}
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button
            key={s || "ALL"}
            data-testid={`applicant-status-filter-${s || "ALL"}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
          >
            {s ? statusLabel(t, s) : t("common.all")}
          </button>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("mgmtRecruitment.shiftInterest")}</span>
        {SHIFT_INTEREST_FILTERS.map((f) => (
          <button
            key={f.key || "ALL"}
            data-testid={`applicant-shift-filter-${f.key || "ALL"}`}
            onClick={() => { setShiftFilter(f.key); setInterviewFilter(""); }}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${shiftFilter === f.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("mgmtRecruitment.interview")}</span>
        {INTERVIEW_FILTERS.map((f) => (
          <button
            key={f.key || "ALL"}
            data-testid={`applicant-interview-filter-${f.key || "ALL"}`}
            onClick={() => { setInterviewFilter(f.key); setShiftFilter(""); }}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${interviewFilter === f.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colApplicant")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colJob")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colStore")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colShiftInterest")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colWeeklyAvailability")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.interview")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colWorkAuth")}</th>
              <th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtRecruitment.colApplied")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleApplicants.map((a) => {
              const shiftTag = getShiftInterestTag(a);
              const interviewTag = getRealInterviewStatusTag(a);
              return (
                <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/management/recruitment/applicants/${a.id}`)} data-testid={`applicant-row-${a.id}`}>
                  <td className="px-4 py-3 font-semibold text-blue-600">{a.legal_name}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{jobTitle(a.job_posting_id)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{jobStore(a.job_posting_id)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {shiftTag && <span data-testid={`applicant-shift-tag-${a.id}`} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${SHIFT_TAG_STYLES[shiftTag.key]}`}>{shiftTag.label}</span>}
                      {needsAvailabilityReview(a) && <span data-testid={`applicant-availability-review-tag-${a.id}`} className="px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-gray-100 border-gray-300 text-gray-600">{t("mgmtRecruitment.availabilityReview")}</span>}
                      {!shiftTag && !needsAvailabilityReview(a) && <span className="text-xs text-gray-300">-</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{a.shifts_per_week ? `${a.shifts_per_week} ${t("mgmtRecruitment.shiftsPerWeek")}` : "-"}</td>
                  <td className="px-4 py-3">
                    <span data-testid={`applicant-interview-tag-${a.id}`} className={`text-[11px] font-semibold ${INTERVIEW_TAG_STYLES[interviewTag.tone]}`}>{interviewTag.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    {a.work_authorization_review_required ? <span className="text-[11px] font-semibold text-red-500" data-testid={`applicant-workauth-review-tag-${a.id}`}>{t("mgmtRecruitment.reviewRequired")}</span> : <span className="text-[11px] text-gray-500">{t("mgmtRecruitment.ok")}</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{(a.created_at || "").slice(0, 10)}</td>
                </tr>
              );
            })}
            {visibleApplicants.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-500" data-testid="no-applicants-row">{t("mgmtRecruitment.noApplicants")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
